import React, { useEffect, useState } from 'react';
import { getGallery, uploadGalleryPhoto, deleteGalleryPhoto } from '../../services/marketingGalleryService';

const AdminMarketingGallery = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const fetchPhotos = async () => {
    try {
      const data = await getGallery();
      setPhotos(data.photos);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load gallery');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPhotos();
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      await uploadGalleryPhoto(file, caption.trim());
      setCaption('');
      await fetchPhotos();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload photo');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this screenshot from the gallery?')) return;
    setDeletingId(id);
    try {
      await deleteGalleryPhoto(id);
      await fetchPhotos();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete photo');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) return <p style={styles.status}>Loading gallery...</p>;

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>📸 Marketing Screenshot Gallery</h2>
      <p style={styles.subtitle}>
        These photos appear publicly on the Register page, shown to anyone before they
        sign up — no login required to view them.
      </p>

      {error && <p style={styles.error}>{error}</p>}

      <div style={styles.uploadRow}>
        <input
          type="text"
          placeholder="Optional caption for the next photo..."
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          style={styles.captionInput}
        />
        <label style={styles.uploadBtn}>
          {uploading ? 'Uploading...' : '+ Add Screenshot'}
          <input type="file" accept="image/*" onChange={handleUpload} style={{ display: 'none' }} disabled={uploading} />
        </label>
      </div>

      <div style={styles.grid}>
        {photos.map((photo) => (
          <div key={photo.id} style={styles.card}>
            <img src={photo.photo_url} alt={photo.caption || ''} style={styles.image} />
            {photo.caption && <p style={styles.caption}>{photo.caption}</p>}
            <button
              onClick={() => handleDelete(photo.id)}
              style={styles.deleteBtn}
              disabled={deletingId === photo.id}
            >
              {deletingId === photo.id ? 'Removing...' : 'Remove'}
            </button>
          </div>
        ))}
        {photos.length === 0 && <p style={styles.status}>No screenshots yet — add one above.</p>}
      </div>
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: 'white', padding: '32px', borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  },
  title: { color: '#e67e22', marginBottom: '6px' },
  subtitle: { color: '#888', fontSize: '13px', marginBottom: '24px', lineHeight: 1.5 },
  uploadRow: { display: 'flex', gap: '10px', marginBottom: '24px', flexWrap: 'wrap' },
  captionInput: {
    flex: 1, minWidth: '200px', padding: '10px 14px', borderRadius: '8px',
    border: '1px solid #ddd', fontSize: '14px',
  },
  uploadBtn: {
    display: 'inline-block', padding: '10px 20px', backgroundColor: '#e67e22', color: 'white',
    border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap',
  },
  grid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '16px',
  },
  card: { border: '1px solid #eee', borderRadius: '10px', padding: '10px', textAlign: 'center' },
  image: { width: '100%', height: '110px', objectFit: 'cover', borderRadius: '8px', marginBottom: '8px' },
  caption: { fontSize: '12px', color: '#666', marginBottom: '8px' },
  deleteBtn: {
    width: '100%', padding: '6px', backgroundColor: 'transparent', color: '#e74c3c',
    border: '1px solid #e74c3c', borderRadius: '6px', cursor: 'pointer', fontSize: '12px',
  },
  status: { textAlign: 'center', color: '#999' },
  error: { color: '#e74c3c', marginBottom: '16px', textAlign: 'center' },
};

export default AdminMarketingGallery;
